import { cva, type VariantProps } from 'class-variance-authority';

const defaultKundeBtnClasses = [
  'kunde:text-sm',
  'kunde:rounded-md',
  'focus-visible:kunde:outline-hidden',
  'focus-visible:kunde:ring-2',
  'focus-visible:kunde:ring-blue-60',
  'focus-visible:kunde:border-transparent',
];

export const buttonVariants = cva(
  [
    'flex items-center rounded border font-medium',
    'disabled:cursor-default enabled:cursor-pointer',
    'disabled:laane:cursor-not-allowed',
    ...defaultKundeBtnClasses,
  ],
  {
    variants: {
      theme: {
        default: [
          'border-transparent text-neutral-110 disabled:text-neutral-60',
          'bg-neutral-10 hover:bg-neutral-40 focus:bg-neutral-40 active:animate-active active:bg-neutral-10',
          'aria-expanded:animate-active aria-expanded:bg-neutral-10',
          'kunde:border-neutral-40 kunde:bg-transparent kunde:text-neutral-200 hover:kunde:bg-other-5/10 active:kunde:bg-other-60/40 disabled:kunde:border-neutral-10 disabled:kunde:text-neutral-60',
          'aria-expanded:enabled:kunde:bg-blue-10 aria-expanded:enabled:kunde:border-blue-10',
        ],
        primary: [
          'border-transparent text-white disabled:text-neutral-60',
          'bg-blue-100 hover:bg-blue-40 focus:bg-blue-40 active:bg-blue-100 disabled:bg-neutral-10',
          'enabled:aria-expanded:bg-blue-100',
          'hover:kunde:bg-blue-60 focus:kunde:border-blue-10 focus:kunde:bg-blue-100 active:kunde:bg-blue-110 disabled:kunde:bg-neutral-10 aria-disabled:kunde:bg-neutral-10 disabled:kunde:text-neutral-60 aria-disabled:kunde:text-neutral-60',
        ],
        secondary: [
          'border-blue-60 text-blue-60 focus-visible:border-transparent',
          'hover:bg-blue-5 enabled:active:bg-blue-10 disabled:border-transparent disabled:bg-neutral-10 disabled:text-neutral-60',
          'kunde:border-blue-10 kunde:bg-teal-5 kunde:text-blue-100 hover:kunde:border-blue-20 hover:kunde:bg-blue-10 active:kunde:border-transparent active:kunde:bg-blue-20 disabled:kunde:border-transparent disabled:kunde:bg-neutral-10 disabled:kunde:text-neutral-60',
        ],
        tertiary: [
          'border-transparent text-blue-60',
          'hover:bg-blue-5 active:bg-blue-5 disabled:bg-transparent disabled:text-neutral-60',
          'kunde:bg-white hover:kunde:bg-blue-5 active:kunde:bg-blue-5 disabled:kunde:bg-white',
        ],
        subtle: [
          'border-transparent text-neutral-110',
          'focus:bg-neutral-10 enabled:hover:bg-neutral-10 disabled:bg-transparent disabled:text-neutral-60',
          'kunde:bg-white kunde:text-neutral-200 hover:kunde:bg-other-10/10 active:kunde:bg-other-60/40 disabled:kunde:bg-white disabled:kunde:text-neutral-60',
        ],
        ghost: [
          'border-transparent text-neutral-200',
          'focus:bg-neutral-10 enabled:hover:bg-neutral-10 disabled:bg-transparent disabled:text-neutral-60',
          'kunde:bg-white kunde:text-neutral-200 hover:kunde:bg-other-10/10 active:kunde:bg-other-60/40 disabled:kunde:bg-white disabled:kunde:text-neutral-60',
        ],
        warning: [
          'border-transparent text-neutral-110 enabled:active:text-blue-110 disabled:text-neutral-60',
          'bg-orange-100 enabled:hover:bg-orange-60 enabled:active:bg-orange-110 disabled:bg-neutral-10',
          'kunde:text-neutral-200 active:kunde:text-neutral-200 disabled:kunde:text-neutral-60',
        ],
        danger: [
          'border-transparent text-white hover:text-neutral-200 disabled:text-neutral-60',
          'bg-red-100 enabled:hover:bg-red-60 enabled:active:bg-red-110 enabled:active:text-white disabled:bg-neutral-10',
        ],
        none: 'group border-0 p-1',
      },
      inverted: {
        true: '',
        false: '',
      },
      compact: {
        true: 'text-xs leading-3 laane:h-6 kunde:h-10',
        false: 'laane:text-xs font-medium leading-4 kunde:h-14',
      },
      rightAligned: {
        true: 'flex-row-reverse',
        false: '',
      },
      loading: {
        true: 'p-4 sm:p-2 md:px-4',
        false: '',
      },
      hasIcon: {
        true: '',
        false: '',
      },
      hasLabel: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        theme: 'default',
        inverted: true,
        className: [
          'kunde:bg-transparent kunde:text-blue-10 kunde:border-blue-10 hover:enabled:kunde:bg-other-60/40',
          'focus-visible:kunde:ring-blue-20 focus-visible:kunde:border-blue-20 active:enabled:kunde:bg-other-5/5 disabled:kunde:border-neutral-100 disabled:kunde:text-neutral-100',
        ],
      },
      {
        theme: 'primary',
        inverted: true,
        className: [
          'kunde:bg-blue-10 kunde:text-blue-100 hover:kunde:bg-blue-20 focus:kunde:border-blue-110 focus:kunde:bg-blue-10 ',
          'disabled:kunde:bg-other-100/60 disabled:kunde:text-neutral-60 focus:kunde:ring-blue-20 active:kunde:bg-blue-40',
        ],
      },
      {
        theme: 'secondary',
        inverted: true,
        className: [
          'kunde:bg-blue-60 kunde:text-blue-5 kunde:hover:bg-text-blue-10 hover:kunde:bg-blue-100',
          'focus-visible:kunde:border-blue-110 focus-visible:kunde:ring-blue-20 focus:kunde:text-blue-5 kunde:focus:bg-blu-60',
          'active:enabled:kunde:bg-blue-100 active:enabled:kunde:text-blue-20',
          'disabled:kunde:bg-other-100/60 disabled:kunde:text-neutral-60',
        ],
      },
      {
        theme: 'tertiary',
        inverted: true,
        className: [
          'kunde:text-blue-10 hover:kunde:bg-other-60/40 focus-visible:kunde:ring-blue-20 focus-visible:kunde:bg-blue-110',
          'active:enabled:kunde:bg-other-60/40 active:enabled:kunde:text-blue-20',
          'disabled:kunde:bg-transparent disabled:kunde:text-neutral-100',
        ],
      },
      {
        theme: 'subtle',
        inverted: true,
        className: [
          'kunde:text-white hover:kunde:bg-other-60/40 focus-visible:kunde:ring-blue-20 focus-visible:kunde:bg-blue-110',
          'active:enabled:kunde:bg-other-60/40 active:enabled:kunde:text-blue-10 disabled:kunde:bg-transparent disabled:kunde:text-neutral-100',
        ],
      },
      {
        theme: 'warning',
        inverted: true,
        className: [
          'kunde:bg-orange-10 kunde:text-neutral-200 hover:kunde:bg-orange-40 kunde:focus-visible:orange-10 ',
          'focus-visible:kunde:border-blue-20 focus-visible:kunde:ring-blue-20 active:enabled:kunde:bg-orange-100',
          'disabled:kunde:bg-other-100/60 disabled:kunde:text-neutral-60',
        ],
      },
      {
        theme: 'danger',
        inverted: true,
        className: [
          'kunde:bg-red-10 kunde:text-red-110 hover:kunde:bg-red-40 focus-visible:kunde:ring-blue-20 focus-visible:kunde:border-blue-20',
          'disabled:kunde:bg-other-100/60 disabled:kunde:text-neutral-60 active:enabled:kunde:bg-red-100 active:enabled:kunde:text-neutral-200',
        ],
      },
      {
        compact: true,
        loading: false,
        rightAligned: true,
        hasIcon: true,
        hasLabel: true,
        className: 'laane:pl-2 laane:pr-1 kunde:px-4',
      },
      {
        compact: true,
        loading: false,
        rightAligned: false,
        hasIcon: true,
        hasLabel: true,
        className: 'laane:pl-1 laane:pr-2 kunde:px-4',
      },
      {
        compact: false,
        loading: false,
        rightAligned: true,
        hasIcon: true,
        hasLabel: true,
        className: 'laane:pl-4 laane:pr-2 kunde:pl-4 kunde:pr-2',
      },
      {
        compact: false,
        loading: false,
        rightAligned: false,
        hasIcon: true,
        hasLabel: true,
        className: 'laane:pl-2 laane:pr-4 kunde:px-6',
      },
      {
        compact: false,
        loading: false,
        rightAligned: false,
        hasIcon: true,
        hasLabel: true,
        className: 'laane:pl-2 laane:pr-4 kunde:px-6',
      },
      {
        compact: false,
        loading: false,
        hasIcon: false,
        hasLabel: true,
        className: 'laane:px-4 laane:py-1 kunde:px-4 kunde:py-4 kunde:px-6',
      },
      {
        compact: true,
        loading: false,
        hasIcon: false,
        className: 'laane:px-2 kunde:px-4 kunde:py-2',
      },
      {
        compact: true,
        loading: true,
        className: 'laane:px-2 laane:py-1 kunde:px-4 kunde:py-2',
      },
      {
        compact: false,
        loading: true,
        className: 'laane:px-4 laane:py-1 kunde:px-4',
      },
    ],
    defaultVariants: {
      theme: 'default',
      compact: false,
      rightAligned: false,
      loading: false,
      hasIcon: false,
    },
  },
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
